import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import {
  ThumbDownFilledIcon,
  ThumbDownIcon,
  ThumbUpFilledIcon,
  ThumbUpIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { aiExpressionAuthoringApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import {
  GENERATE_FEEDBACK_ADD_NOTE_LABEL,
  GENERATE_FEEDBACK_COMMENT_MAX_LENGTH,
  GENERATE_FEEDBACK_COMMENT_PLACEHOLDER,
  GENERATE_FEEDBACK_FAILED_HELPER,
  GENERATE_FEEDBACK_HIDE_NOTE_LABEL,
  GENERATE_FEEDBACK_OUTCOME,
  GENERATE_FEEDBACK_PROMPT_LABEL,
  GENERATE_FEEDBACK_STATUS,
  GENERATE_FEEDBACK_SUBMIT_BUTTON_LABEL,
  GENERATE_FEEDBACK_TAGS,
  GENERATE_FEEDBACK_THANKS_HELPER,
  GENERATE_FEEDBACK_THUMBS_DOWN_LABEL,
  GENERATE_FEEDBACK_THUMBS_UP_LABEL,
} from '../GenerateExpression.constants';
import {
  feedbackActionRowStyles,
  feedbackHeaderRowStyles,
  feedbackNoteToggleStyles,
  feedbackNoteWrapperStyles,
  feedbackTagsRowStyles,
  feedbackThumbsRowStyles,
  feedbackWrapperStyles,
  modalDescriptionFieldStyles,
  modalSectionLabelStyles,
} from '../GenerateExpression.styles';
import { GenerateExpressionTestIds } from '../GenerateExpression.testIds';
import { isCancelledError } from '../GenerateExpression.utils';

import FeedbackTagChip from './FeedbackTagChip';

const ApplyFeedback = ({ aiUsageMetricsId }) => {
  const [outcome, setOutcome] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState(GENERATE_FEEDBACK_STATUS.IDLE);

  const abortControllerRef = useRef(null);

  const cancelInflight = useCallback(() => {
    if (_.isNil(abortControllerRef.current)) {
      return;
    }

    abortControllerRef.current.abort();
    abortControllerRef.current = null;
  }, []);

  useEffect(() => () => cancelInflight(), [cancelInflight]);

  const onThumbsUp = useCallback(() => {
    setOutcome(GENERATE_FEEDBACK_OUTCOME.ACCEPTED);
    setSelectedTags([]);
    setIsNoteExpanded(false);
  }, []);

  const onThumbsDown = useCallback(() => {
    setOutcome(GENERATE_FEEDBACK_OUTCOME.DISCARDED);
  }, []);

  const onToggleTag = useCallback(tagValue => {
    setSelectedTags(prev =>
      prev.includes(tagValue) ? prev.filter(t => t !== tagValue) : [...prev, tagValue],
    );
  }, []);

  const onToggleNote = useCallback(() => {
    setIsNoteExpanded(value => !value);
  }, []);

  const onCommentChange = useCallback(event => {
    setComment(event.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    if (_.isNil(outcome)) {
      return;
    }

    cancelInflight();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setStatus(GENERATE_FEEDBACK_STATUS.SUBMITTING);

    const trimmedComment = _.trim(comment);

    aiExpressionAuthoringApi
      .submitExpressionGenerationFeedback({
        aiUsageMetricsId,
        outcome,
        comment: _.isEmpty(trimmedComment) ? null : trimmedComment,
        tags: _.isEmpty(selectedTags) ? null : selectedTags,
        signal: controller.signal,
      })
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }

        setStatus(GENERATE_FEEDBACK_STATUS.SUBMITTED);
      })
      .catch(error => {
        if (isCancelledError(error) || controller.signal.aborted) {
          return;
        }

        setStatus(GENERATE_FEEDBACK_STATUS.ERROR);
      });
  }, [aiUsageMetricsId, cancelInflight, comment, outcome, selectedTags]);

  const isThumbsUp = outcome === GENERATE_FEEDBACK_OUTCOME.ACCEPTED;
  const isThumbsDown = outcome === GENERATE_FEEDBACK_OUTCOME.DISCARDED;
  const isSubmitting = status === GENERATE_FEEDBACK_STATUS.SUBMITTING;
  const isSubmitted = status === GENERATE_FEEDBACK_STATUS.SUBMITTED;
  const showTagsRow = isThumbsDown && !isSubmitted;
  const showNoteToggle = !_.isNil(outcome) && !isSubmitted;
  const showSubmitButton = !_.isNil(outcome) && !isSubmitted;

  if (isSubmitted) {
    return (
      <Box data-testid={GenerateExpressionTestIds.MODAL_FEEDBACK_THANKS} sx={feedbackWrapperStyles}>
        <Typography color="text.secondary" variant="regular14">
          {GENERATE_FEEDBACK_THANKS_HELPER}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid={GenerateExpressionTestIds.MODAL_FEEDBACK} sx={feedbackWrapperStyles}>
      <Box sx={feedbackHeaderRowStyles}>
        <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
          {GENERATE_FEEDBACK_PROMPT_LABEL}
        </Typography>
        <Box sx={feedbackThumbsRowStyles}>
          <Button
            color={isThumbsUp ? 'primary' : 'inherit'}
            data-testid={GenerateExpressionTestIds.MODAL_FEEDBACK_THUMBS_UP}
            startIcon={
              isThumbsUp ? (
                <ThumbUpFilledIcon size={IconSize[20]} />
              ) : (
                <ThumbUpIcon size={IconSize[20]} />
              )
            }
            variant={isThumbsUp ? 'contained' : 'text'}
            onClick={onThumbsUp}
          >
            {GENERATE_FEEDBACK_THUMBS_UP_LABEL}
          </Button>
          <Button
            color={isThumbsDown ? 'primary' : 'inherit'}
            data-testid={GenerateExpressionTestIds.MODAL_FEEDBACK_THUMBS_DOWN}
            startIcon={
              isThumbsDown ? (
                <ThumbDownFilledIcon size={IconSize[20]} />
              ) : (
                <ThumbDownIcon size={IconSize[20]} />
              )
            }
            variant={isThumbsDown ? 'contained' : 'text'}
            onClick={onThumbsDown}
          >
            {GENERATE_FEEDBACK_THUMBS_DOWN_LABEL}
          </Button>
        </Box>
      </Box>

      {showTagsRow && (
        <Box sx={feedbackTagsRowStyles}>
          {GENERATE_FEEDBACK_TAGS.map(tag => (
            <FeedbackTagChip
              isSelected={selectedTags.includes(tag.value)}
              key={tag.value}
              label={tag.label}
              value={tag.value}
              onToggle={onToggleTag}
            />
          ))}
        </Box>
      )}

      {showNoteToggle && (
        <Button
          color="primary"
          data-testid={GenerateExpressionTestIds.MODAL_FEEDBACK_ADD_NOTE_TOGGLE}
          sx={feedbackNoteToggleStyles}
          variant="text"
          onClick={onToggleNote}
        >
          {isNoteExpanded ? GENERATE_FEEDBACK_HIDE_NOTE_LABEL : GENERATE_FEEDBACK_ADD_NOTE_LABEL}
        </Button>
      )}

      {isNoteExpanded && (
        <Box sx={feedbackNoteWrapperStyles}>
          <TextFieldCDS
            multiline
            ariaLabel={GENERATE_FEEDBACK_COMMENT_PLACEHOLDER}
            customStyles={modalDescriptionFieldStyles}
            dataTestId={GenerateExpressionTestIds.MODAL_FEEDBACK_COMMENT_INPUT}
            maxLength={GENERATE_FEEDBACK_COMMENT_MAX_LENGTH}
            maxRows={4}
            name="expressionFeedbackComment"
            placeholder={GENERATE_FEEDBACK_COMMENT_PLACEHOLDER}
            rows={2}
            value={comment}
            onChange={onCommentChange}
          />
        </Box>
      )}

      <Typography
        color="error.main"
        isHidden={status !== GENERATE_FEEDBACK_STATUS.ERROR}
        variant="regular14"
      >
        {GENERATE_FEEDBACK_FAILED_HELPER}
      </Typography>

      {showSubmitButton && (
        <Box sx={feedbackActionRowStyles}>
          {isSubmitting && <CircularProgress size={18} />}
          <Button
            data-testid={GenerateExpressionTestIds.MODAL_FEEDBACK_SUBMIT}
            disabled={isSubmitting}
            variant="contained"
            onClick={onSubmit}
          >
            {GENERATE_FEEDBACK_SUBMIT_BUTTON_LABEL}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ApplyFeedback.propTypes = {
  aiUsageMetricsId: PropTypes.string.isRequired,
};

export default ApplyFeedback;
